import { normalizeStructTag } from '@mysten/sui/utils';
import { BN, Decimal, Percentage, PoolUtil, PositionUtil, TickMath } from 'parrot-sui-sdk';
import { type NumericFormatProps, numericFormatter } from 'react-number-format';

export const numericFormat = (value: string | number, props: NumericFormatProps = {}) => {
  return numericFormatter(value.toString(), {
    thousandSeparator: true,
    decimalScale: 2,
    ...props,
  });
};

export const normalizeSuiCoinType = (coinType: string) => {
  if (coinType === '') return coinType;

  const type = normalizeStructTag(coinType);

  if (type === '0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI') {
    return '0x2::sui::SUI';
  }

  return type;
};

export const percentage = (slippage: number) => {
  let numerator = 2;
  let num = slippage;
  const _slippage = slippage.toString();
  const step = _slippage.indexOf('.');
  if (step > -1) {
    const len = _slippage.slice(step + 1).length;
    num = num * 10 ** len;
    numerator = numerator + len;
  }
  return new Percentage(new BN(num), new BN(10 ** numerator));
};

export const calculateAmountAndRatioFromLiquidity = (
  liquidity: string | undefined,
  currentTick: number | undefined,
  lowerTick: number | undefined,
  upperTick: number | undefined,
  roundUp: boolean = false,
) => {
  if (
    !liquidity ||
    liquidity === '0' ||
    !currentTick ||
    !lowerTick ||
    !upperTick ||
    upperTick <= lowerTick
  ) {
    return {
      liquidity_a: '0',
      liquidity_b: '0',
      amount_a: '0',
      amount_b: '0',
      ratio_a: 50,
      ratio_b: 50,
    };
  }

  const currentSqrtPrice = TickMath.tickIndexToSqrtPriceX64(currentTick);
  const lowerSqrtPrice = TickMath.tickIndexToSqrtPriceX64(lowerTick);
  const upperSqrtPrice = TickMath.tickIndexToSqrtPriceX64(upperTick);

  const _liquidity = new BN(liquidity);
  const coin = PoolUtil.getCoinAmountFromLiquidity(
    _liquidity,
    currentSqrtPrice,
    lowerSqrtPrice,
    upperSqrtPrice,
    roundUp,
  );

  const liquidityA = PositionUtil.getLiquidityFromCoinA(
    coin.coin_a,
    lowerSqrtPrice,
    upperSqrtPrice,
    roundUp,
  );

  const liquidityB = PositionUtil.getLiquidityFromCoinB(
    coin.coin_b,
    lowerSqrtPrice,
    upperSqrtPrice,
    roundUp,
  );

  let ratioA = new Decimal(0.5);
  let ratioB = new Decimal(0.5);

  if (currentTick < lowerTick) {
    ratioA = new Decimal(1);
    ratioB = new Decimal(0);
  } else if (currentTick >= upperTick) {
    ratioA = new Decimal(0);
    ratioB = new Decimal(1);
  } else {
    const ratio = PoolUtil.calculateDepositRatioFixTokenA(lowerTick, upperTick, currentSqrtPrice);
    ratioA = ratio.ratioA;
    ratioB = ratio.ratioB;
  }

  return {
    liquidity_a: liquidityA.toString(),
    liquidity_b: liquidityB.toString(),
    amount_a: coin.coin_a.toString(),
    amount_b: coin.coin_b.toString(),
    ratio_a: new Decimal(ratioA.mul(10000).toNumber().toFixed(0)).div(100).toString(),
    ratio_b: new Decimal(ratioB.mul(10000).toNumber().toFixed(0)).div(100).toString(),
  };
};

export const formatContract = (address: string) => {
  return address.replace(/^(.{10}).*?(.{0,10})$/, `$1${address.length > 20 ? '...' : ''}$2`);
};
